import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Badge, IconButton } from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { fetchDmUnread } from '../redux/actions/socialActions';
import { useWs } from '../context/WsContext';

// MessagesButton — the DM entry point in the top navbar, beside the bell.
//
// HIDDEN BELOW `sm`, and that is measured, not guessed. The navbar's right-hand
// stack was already at its limit before this feature: at a 375px viewport it
// ends at 371px, with 4px to spare. Adding a 38px button pushes it to 388px and
// clips the account avatar off the right edge of the screen. At 600px the same
// stack ends at 592px and fits, so `sm` is the real boundary.
//
// Phones therefore reach DMs through the People hub (a "Messages" row at the
// top of /people, which is the second bottom-nav slot) rather than through the
// navbar - the same move PR A made when the bottom bar's five-tab cap forced
// Discover to be reachable from People instead of from the bar itself. Parity
// is preserved: every viewport can reach the inbox, just not through identical
// chrome. Desktop additionally gets the sidebar row from NavConfig.
//
// The badge counts CONVERSATIONS, not messages. One chatty friend should read
// as one thread needing attention, not as "47" - and the per-conversation
// coalescing the backend does only pays off if the UI honours it.

export default function MessagesButton() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ws = useWs();

  const { userInfo } = useSelector((s) => s.userLogin || {});
  const unread = useSelector((s) => s.social?.dmUnread?.conversations || 0);

  const enabled = Boolean(userInfo?.access) && !userInfo?.isAnon;

  useEffect(() => {
    if (enabled) dispatch(fetchDmUnread());
  }, [enabled, dispatch]);

  // `dm.badge` is the USER-scoped push and the only one this button can rely
  // on: `dm.message` is ROOM-scoped, so it reaches a tab only while that tab
  // has the relevant thread open. Subscribing to both means the badge updates
  // whether or not the user is looking at the conversation. subscribeOpen
  // resyncs on every (re)connect so a count that went stale while offline
  // corrects itself - same shape as NotificationBell's unread-count handling.
  useEffect(() => {
    if (!ws || !enabled) return undefined;
    const refresh = () => dispatch(fetchDmUnread());
    const offBadge = ws.subscribe('dm.badge', refresh);
    const offMessage = ws.subscribe('dm.message', refresh);
    const offOpen = ws.subscribeOpen(refresh);
    return () => { offBadge(); offMessage(); offOpen(); };
  }, [ws, enabled, dispatch]);

  if (!enabled) return null;

  return (
    <IconButton
      onClick={() => navigate('/messages')}
      aria-label={unread > 0 ? `Messages, ${unread} unread` : 'Messages'}
      data-testid="messages-button"
      sx={{
        // See the note above: below `sm` this button overflows the navbar and
        // clips the account avatar. Mobile reaches DMs from the People hub.
        display: { xs: 'none', sm: 'inline-flex' },
        color: 'text.primary',
        transition: 'color 0.2s ease',
        '&:hover': { color: 'primary.main' },
      }}
    >
      <Badge badgeContent={unread} color="error">
        <ChatBubbleOutlineIcon sx={{ fontSize: 22 }} />
      </Badge>
    </IconButton>
  );
}
